import React from 'react';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { formatValue, getNestedValue } from '../utils/formatters';

const MiniChartRenderer = ({ data, config }) => {
  if (!data) return null;

  const { dataMapping = {}, chart = {} } = config;
  const series = getNestedValue(data, dataMapping.series) || [];
  const headline = dataMapping.headline;

  const {
    type = 'area',
    xKey = 'date',
    yKey = 'value',
    color = '#4e73df',
    height = 120,
    showAxis = false,
    showTooltip = true,
  } = chart;

  const headlineValue = headline ? getNestedValue(data, headline.value) : null;
  const headlineChange = headline ? getNestedValue(data, headline.change) : null;
  const headlineFormat = headline?.format || 'number';

  const changeNum = headlineChange !== null ? parseFloat(headlineChange) : null;
  const isPositive = changeNum !== null && changeNum >= 0;

  const renderChart = () => {
    const commonProps = { data: series, margin: { top: 5, right: 5, left: 5, bottom: 5 } };

    const tooltipContent = showTooltip ? (
      <Tooltip
        formatter={(value) => [formatValue(value, headlineFormat), '']}
        labelFormatter={(label) => label}
        contentStyle={{ fontSize: '12px' }}
      />
    ) : null;

    const xAxis = showAxis ? <XAxis dataKey={xKey} fontSize={10} tickLine={false} /> : <XAxis dataKey={xKey} hide />;
    const yAxis = showAxis ? <YAxis fontSize={10} tickLine={false} axisLine={false} /> : <YAxis hide />;

    if (type === 'bar') {
      return (
        <BarChart {...commonProps}>
          {xAxis}
          {yAxis}
          {tooltipContent}
          <Bar dataKey={yKey} fill={color} barSize={20} />
        </BarChart>
      );
    }

    if (type === 'line') {
      return (
        <LineChart {...commonProps}>
          {xAxis}
          {yAxis}
          {tooltipContent}
          <Line type="monotone" dataKey={yKey} stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      );
    }

    return (
      <AreaChart {...commonProps}>
        {xAxis}
        {yAxis}
        {tooltipContent}
        <Area type="monotone" dataKey={yKey} stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2} dot={false} />
      </AreaChart>
    );
  };

  return (
    <div>
      {headlineValue !== null && (
        <div className="flex items-center justify-between mb-2">
          <p className="text-2xl font-bold text-gray-900">
            {formatValue(headlineValue, headlineFormat)}
          </p>
          {changeNum !== null && (
            <span className={`flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
              isPositive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {isPositive ? <FaArrowUp className="w-2.5 h-2.5 mr-0.5" /> : <FaArrowDown className="w-2.5 h-2.5 mr-0.5" />}
              {Math.abs(changeNum).toFixed(1)}%
            </span>
          )}
        </div>
      )}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MiniChartRenderer;
